/**
 * Foundry Agent Hooks
 *
 * React Query hooks for fetching and managing Foundry agents.
 * Follows the same patterns as useAzureResources.ts.
 */

import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient, type UseQueryResult, type UseMutationResult } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import {
  listAllFoundryAgentsBoth,
  getFoundryAgentByVersion,
  createFoundryAgent,
  updateFoundryAgent,
  type FoundryAgent,
  type FoundryAgentVersion,
  type CreateAgentPayload,
  type UpdateAgentPayload,
} from '@/services/foundryAgentService';
import { getCognitiveProjects, buildProjectEndpoint, parseResourceId } from '@/services/azureResourceService';

// =============================================================================
// QUERY KEYS
// =============================================================================

export const foundryAgentKeys = {
  all: ['foundryAgents'] as const,
  list: (projectEndpoint: string) =>
    [...foundryAgentKeys.all, 'list', projectEndpoint] as const,
  detail: (projectEndpoint: string, agentId: string) =>
    [...foundryAgentKeys.all, 'detail', projectEndpoint, agentId] as const,
};

// =============================================================================
// CACHE CONFIGURATION
// =============================================================================

const CACHE_CONFIG = {
  staleTime: 1000 * 60 * 5, // 5 minutes (agents change more frequently than subscriptions)
  gcTime: 1000 * 60 * 30, // 30 minutes
  retry: 1,
};

// =============================================================================
// ENDPOINT RESOLUTION
// =============================================================================

/**
 * Check whether an endpoint already has the /api/projects/{project} path.
 */
function isFullProjectEndpoint(endpoint: string): boolean {
  return /\/api\/projects\/[^/]+/i.test(endpoint);
}

/**
 * Extract the account name from a cognitive services or services.ai endpoint URL.
 * e.g. "https://myaccount.cognitiveservices.azure.com/" → "myaccount"
 */
function extractAccountName(endpoint: string): string | undefined {
  try {
    const hostname = new URL(endpoint).hostname;
    return hostname.split('.')[0] || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Resolve a legacy cognitive services endpoint to a full Foundry project endpoint.
 *
 * Legacy connections store just the account endpoint
 * (e.g. `https://account.cognitiveservices.azure.com/`) without the project path.
 * This hook discovers the project via ARM API and builds the correct endpoint:
 * `https://account.services.ai.azure.com/api/projects/{project}`
 */
export function useResolvedFoundryEndpoint(
  rawEndpoint: string | undefined,
  resourceId: string | undefined
): { resolvedEndpoint: string | undefined; isResolving: boolean; error: string | undefined } {
  const { getArmToken } = useAuth();

  // Extract subscription ID from the connection's ARM resource ID
  const subscriptionId = useMemo(() => {
    if (!resourceId) return undefined;
    return parseResourceId(resourceId).subscriptionId;
  }, [resourceId]);

  // Extract account name from the raw endpoint
  const accountName = useMemo(() => {
    if (!rawEndpoint) return undefined;
    return extractAccountName(rawEndpoint);
  }, [rawEndpoint]);

  // If endpoint already has project path, no resolution needed
  const needsResolution = rawEndpoint ? !isFullProjectEndpoint(rawEndpoint) : false;

  // Fetch projects for the account to discover the project name
  const { data: projects, isLoading, error: queryError } = useQuery({
    queryKey: ['foundryEndpointResolution', subscriptionId, accountName],
    queryFn: async () => {
      const token = await getArmToken();
      if (!token) throw new Error('Failed to get ARM token');
      if (!subscriptionId) throw new Error('No subscription ID');
      const allProjects = await getCognitiveProjects(token, subscriptionId);
      // Filter to only projects under this account
      return allProjects.filter((p) => p.accountName === accountName);
    },
    enabled: needsResolution && !!subscriptionId && !!accountName,
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
    retry: 1,
  });

  // Build the resolved endpoint
  const resolvedEndpoint = useMemo(() => {
    if (!rawEndpoint) return undefined;
    if (!needsResolution) return rawEndpoint;
    if (!projects || projects.length === 0 || !accountName) return undefined;

    // Use the first project under this account
    const projectName = projects[0]!.name.includes('/')
      ? projects[0]!.name.split('/')[1]!
      : projects[0]!.name;
    return buildProjectEndpoint(accountName, projectName);
  }, [rawEndpoint, needsResolution, projects, accountName]);

  const error = queryError
    ? `Failed to discover Foundry project: ${queryError.message}`
    : (needsResolution && !isLoading && !resolvedEndpoint)
      ? 'No AI Foundry project found for this account'
      : undefined;

  return { resolvedEndpoint, isResolving: isLoading && needsResolution, error };
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Fetch all agents in a Foundry project (both v1 and v2).
 * Auto-paginates both endpoints and merges results.
 */
export function useFoundryAgents(
  projectEndpoint: string,
  enabled = true
): UseQueryResult<FoundryAgent[], Error> {
  const { getFoundryToken } = useAuth();

  return useQuery({
    queryKey: foundryAgentKeys.list(projectEndpoint),
    queryFn: async () => {
      const token = await getFoundryToken();
      if (!token) throw new Error('Authentication required for Foundry. Please sign out and sign back in.');
      return listAllFoundryAgentsBoth(projectEndpoint, token);
    },
    enabled: enabled && !!projectEndpoint,
    ...CACHE_CONFIG,
  });
}

/**
 * Fetch a single Foundry agent by ID.
 * Uses the correct API path based on agent version.
 */
export function useFoundryAgent(
  projectEndpoint: string,
  agentId: string,
  enabled = true,
  agentVersion: FoundryAgentVersion = 'v2'
): UseQueryResult<FoundryAgent, Error> {
  const { getFoundryToken } = useAuth();

  return useQuery({
    queryKey: foundryAgentKeys.detail(projectEndpoint, agentId),
    queryFn: async () => {
      const token = await getFoundryToken();
      if (!token) throw new Error('Authentication required for Foundry. Please sign out and sign back in.');
      return getFoundryAgentByVersion(projectEndpoint, agentId, token, agentVersion);
    },
    enabled: enabled && !!projectEndpoint && !!agentId,
    ...CACHE_CONFIG,
  });
}

/**
 * Create a new Foundry agent.
 * Invalidates the agent list cache on success.
 */
export function useCreateFoundryAgent(
  projectEndpoint: string
): UseMutationResult<FoundryAgent, Error, CreateAgentPayload> {
  const { getFoundryToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateAgentPayload) => {
      const token = await getFoundryToken();
      if (!token) throw new Error('Authentication required for Foundry. Please sign out and sign back in.');
      return createFoundryAgent(projectEndpoint, payload, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: foundryAgentKeys.list(projectEndpoint),
      });
    },
  });
}

/**
 * Update an existing Foundry agent.
 * Invalidates both the agent list and detail caches on success.
 */
export function useUpdateFoundryAgent(
  projectEndpoint: string,
  agentId: string
): UseMutationResult<FoundryAgent, Error, UpdateAgentPayload> {
  const { getFoundryToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateAgentPayload) => {
      const token = await getFoundryToken();
      if (!token) throw new Error('Authentication required for Foundry. Please sign out and sign back in.');
      return updateFoundryAgent(projectEndpoint, agentId, payload, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: foundryAgentKeys.list(projectEndpoint),
      });
      queryClient.invalidateQueries({
        queryKey: foundryAgentKeys.detail(projectEndpoint, agentId),
      });
    },
  });
}
